import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Pen,
  MoreVertical,
  Loader2,
  Trash2,
  Copy,
  Eye
} from "lucide-react";

export const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input
    className={`flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    ref={ref}
    {...props}
  />
));
Input.displayName = "Input";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline";
}
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", ...props }, ref) => {
    const variants = {
      default: "bg-green-500 text-white hover:bg-green-600",
      destructive: "bg-red-600 text-white hover:bg-red-700",
      outline: "border border-slate-300 bg-white hover:bg-slate-100",
    };
    return (
      <button
        className={`inline-flex items-center justify-center rounded-md px-4 py-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50 disabled:pointer-events-none ${variants[variant]} ${className}`}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: "default" | "success" | "danger";
}
export const Badge: React.FC<BadgeProps> = ({
  className,
  variant = "default",
  ...props
}) => {
  const variants = {
    default: "bg-slate-100 text-slate-700",
    success: "bg-green-100 text-green-800",
    danger: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${variants[variant]} ${className}`}
      {...props}
    />
  );
};

export const ShadcnTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full">
    <table
      ref={ref}
      className={`w-full caption-bottom text-sm ${className}`}
      {...props}
    />
  </div>
));
ShadcnTable.displayName = "ShadcnTable";
export const ShadcnTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={`bg-gray-100 ${className}`} {...props} />
));
ShadcnTableHeader.displayName = "ShadcnTableHeader";
export const ShadcnTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={`[&_tr:last-child]:border-0 ${className}`}
    {...props}
  />
));
ShadcnTableBody.displayName = "ShadcnTableBody";
export const ShadcnTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={`border-b border-gray-300 shadow-sm transition-colors hover:bg-slate-100/50 ${className}`}
    {...props}
  />
));
ShadcnTableRow.displayName = "ShadcnTableRow";
export const ShadcnTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={`h-12 px-6 text-left align-middle font-medium text-slate-500 ${className}`}
    {...props}
  />
));
ShadcnTableHead.displayName = "ShadcnTableHead";
export const ShadcnTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={`px-6 py-3 align-middle ${className}`} {...props} />
));
ShadcnTableCell.displayName = "ShadcnTableCell";

const ActionMenuWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [opensUp, setOpensUp] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpensUp(spaceBelow < 120);
    }
    setIsOpen(!isOpen);
  };

  const menuPositionClass = opensUp ? "bottom-full mb-2" : "mt-2";

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleToggle}
        className="p-2 rounded-full hover:bg-slate-100"
      >
        <MoreVertical className="w-5 h-5 text-slate-600" />
      </button>
      {isOpen && (
        <div
          className={`absolute right-0 w-36 bg-white rounded-md shadow-lg border z-20 ${menuPositionClass}`}
        >
          {React.Children.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child, {
                  closeMenu: () => setIsOpen(false),
                } as any)
              : child
          )}
        </div>
      )}
    </div>
  );
};


// Generic Action Menu for simple Edit/Delete
interface GenericActionMenuProps {
  onEdit: () => void;
  onDelete: () => void;
  closeMenu?: () => void;
}
export const GenericActionMenu: React.FC<GenericActionMenuProps> = ({
  onEdit,
  onDelete,
  closeMenu,
}) => (
  <>
    <button
      onClick={() => {
        onEdit();
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
    >
      <Pen className="w-4 h-4" />
      <span>Edit</span>
    </button>
    <button
      onClick={() => {
        onDelete();
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
    >
      <Trash2 className="w-4 h-4" />
      <span>Delete</span>
    </button>
  </>
);



// Wrapper to be used in pages
export const ActionMenu: React.FC<{
  onEdit: () => void;
  onDelete: () => void;
}> = ({ onEdit, onDelete }) => (
  <ActionMenuWrapper>
    <GenericActionMenu onEdit={onEdit} onDelete={onDelete} />
  </ActionMenuWrapper>
);

// Action Menu for User Management
interface UserActionMenuProps {
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  user: { id: string; name: string };
  closeMenu?: () => void;
}
export const UserActionMenuContent: React.FC<UserActionMenuProps> = ({
  user,
  onEdit,
  onDelete,
  closeMenu,
}) => (
  <>
    <button
      onClick={() => {
        onEdit(user.id);
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
    >
      <Pen className="w-4 h-4" />
      <span>Edit</span>
    </button>
    <button
      onClick={() => {
        onDelete(user.id, user.name);
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
    >
      <Trash2 className="w-4 h-4" />
      <span>Delete</span>
    </button>
  </>
);
export const UserActionMenu: React.FC<
  Omit<UserActionMenuProps, "closeMenu">
> = (props) => (
  <ActionMenuWrapper>
    <UserActionMenuContent {...props} />
  </ActionMenuWrapper>
);

// Action Menu for Design Master
interface DesignActionMenuProps {
  onEdit: (id: string) => void;
  onDuplicate: (id: string, designNo: string) => void;
  onDelete: (id: string, designNo: string) => void;
  design: { id: string; designNo: string };
  closeMenu?: () => void;
}
export const DesignActionMenuContent: React.FC<DesignActionMenuProps> = ({
  design,
  onEdit,
  onDuplicate,
  onDelete,
  closeMenu,
}) => (
  <>
    <button
      onClick={() => {
        onEdit(design.id);
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
    >
      <Pen className="w-4 h-4" />
      <span>Edit</span>
    </button>
    <button
      onClick={() => {
        onDuplicate(design.id, design.designNo);
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
    >
      <Copy className="w-4 h-4" />
      <span>Duplicate</span>
    </button>
    <button
      onClick={() => {
        onDelete(design.id, design.designNo);
        closeMenu?.();
      }}
      className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
    >
      <Trash2 className="w-4 h-4" />
      <span>Delete</span>
    </button>
  </>
);
export const DesignActionMenu: React.FC<
  Omit<DesignActionMenuProps, "closeMenu">
> = (props) => (
  <ActionMenuWrapper>
    <DesignActionMenuContent {...props} />
  </ActionMenuWrapper>
);

// Action Menu for Order Management
interface OrderActionMenuProps {
  orderId: string;
  closeMenu?: () => void;
}
export const OrderActionMenuContent: React.FC<OrderActionMenuProps> = ({
  orderId,
  closeMenu,
}) => {
  const navigate = useNavigate();
  const handleView = () => {
    navigate(`/order-tracker/${orderId}`);
    closeMenu?.();
  };
  const handleEdit = () => {
    navigate(`/edit-order/${orderId}`);
    closeMenu?.();
  };
  return (
    <>
      <button
        onClick={handleView}
        className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-100"
      >
        <Eye className="w-4 h-4" /> View
      </button>
      <button
        onClick={handleEdit}
        className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-100"
      >
        <Pen className="w-4 h-4" /> Edit
      </button>
    </>
  );
};
export const OrderActionMenu: React.FC<{ orderId: string }> = ({ orderId }) => (
  <ActionMenuWrapper>
    <OrderActionMenuContent orderId={orderId} />
  </ActionMenuWrapper>
);



// --- HELPER FUNCTIONS ---
export const formatDateTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleString("en-GB") : "—";
